/**
 * Cliente de la API.
 *
 * En desarrollo, Vite hace de proxy de `/api` hacia el backend (ver
 * `vite.config.ts`), así que las rutas son relativas en ambos entornos y no hay
 * que configurar una URL base distinta por ambiente.
 */

import type {
  Appearance,
  DocumentInk,
  BrandAppearance,
  ClosingPackage,
  Config,
  DataException,
  DrilldownRow,
  MappingAuditRow,
  Overview,
  ReportPage,
  RunDetail,
  RunSummary,
  SessionInfo,
  EntryPage,
  ExportLevel,
  Identity,
  MappingDivergence,
  JournalVoucher,
  ReportTree,
  Schedule,
  SyncProgress,
  Severity,
  StatementLine,
  TreeMode,
  ClassificationRequest,
  ClassificationRevision,
  ClassificationScreen,
} from "./types";

export class ApiError extends Error {
  // Campo declarado y asignado en el cuerpo: `erasableSyntaxOnly` de
  // TypeScript 6 no admite propiedades de parámetro en el constructor.
  readonly status: number;

  /** Código del backend cuando lo hay. Distingue dos 503 que no se parecen. */
  readonly code: string;

  constructor(status: number, message: string, code = "") {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  /** Aún no hay datos: el caso normal antes de la primera sincronización. */
  get isNotReady(): boolean {
    return this.status === 404;
  }

  /** No hay sesión, o la que había dejó de valer. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Hay sesión, pero no alcanza para esto. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** No se pudo validar la sesión de forma segura. Nunca se sirven datos aquí. */
  get isDegraded(): boolean {
    // Un 503 con este código NO es un problema de sesión: es que los datos van
    // por detrás del código. Sin la distinción, un reporte que pide una columna
    // recién añadida echaría al usuario al portón, que es peor que el error que
    // vino a sustituir.
    return this.status === 503 && !this.isSchemaBehind;
  }

  /** Los datos servidos no tienen todavía lo que esta versión del reporte pide. */
  get isSchemaBehind(): boolean {
    return this.code === "DATA_SCHEMA_NOT_READY";
  }
}

/**
 * The session ended mid-navigation: go back to the gate.
 *
 * Nobody did this until now. `App` inspected the 401 of `/api/config` — the
 * first call, the one at boot — and no screen inspected its own: the seventeen
 * that render `ErrorState` showed "No se pudo cargar · sesión expirada o
 * inválida" inside the shell, with the menu, the letterhead and the tabs still
 * in place, as if the report were the thing that had failed. It was not. There
 * was no session, and the only honest screen at that moment is the gate.
 *
 * This navigates for real rather than flipping React state. A navigation throws
 * away the whole mount, and with it any figure still held in memory. The gate
 * promises that without a session there is no last balance, no entries and no
 * cached data; a state flip would leave all of it alive on the heap, unrendered
 * but present.
 *
 * `replace` and not `assign`: the screen that could not load does not deserve a
 * slot in the history. Going back from the gate should reach wherever the user
 * was before, not a page we already know fails.
 *
 * `next` keeps the place, so returning from the provider lands where the work
 * was interrupted instead of on the dashboard.
 */
let returningToGate = false;

function sessionLost(): void {
  // Several requests in flight fail at once; one navigation is enough.
  if (returningToGate) return;
  // Already at the gate: navigating again would be a loop.
  if (window.location.pathname === "/login") return;
  returningToGate = true;
  const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(`/login?expired=1&next=${encodeURIComponent(here)}`);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // La sesión viaja en una cookie `HttpOnly` que JavaScript no puede leer;
    // hay que pedirle al navegador que la envíe. En desarrollo el proxy de
    // Vite comparte origen, así que `same-origin` basta en ambos entornos.
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    let detail = response.statusText;
    let code = "";
    try {
      // El portón responde `{error}`; FastAPI responde `{detail}`.
      const body = (await response.json()) as {
        detail?: string;
        error?: string;
        code?: string;
      };
      if (body.detail) detail = body.detail;
      else if (body.error) detail = body.error;
      code = body.code ?? "";
    } catch {
      // Una respuesta sin cuerpo JSON deja el statusText como mensaje.
    }
    // 401 means the session is gone. A missing permission answers 403, and that
    // one must keep rendering in place — it is a legitimate screen, not a
    // reason to throw somebody out.
    if (response.status === 401) sessionLost();
    throw new ApiError(response.status, detail, code);
  }
  return (await response.json()) as T;
}

/**
 * Descarga un archivo por `fetch`, no navegando a la URL.
 *
 * Un `<a href>` a un endpoint de la API tiene tres fallos que solo se ven en
 * producción:
 *
 *  1. El error se muestra en crudo. La ruta responde JSON —`{"error": …}`— y el
 *     navegador lo enseña como una página o lo guarda como si fuera el archivo.
 *     Con los permisos por reporte ya exigidos, un 403 es un resultado normal,
 *     no una anomalía.
 *  2. La sesión caducada acaba en un `.xlsx` que contiene `{"code": 401}`.
 *  3. No hay forma de saber que está trabajando. El libro completo tarda cerca
 *     de un minuto —son más de veinte consultas seguidas contra BigQuery— y sin
 *     señal alguna el botón parece roto y se pulsa otra vez.
 *
 * Trayendo la respuesta a JavaScript se puede distinguir el error del archivo,
 * y quien llama decide qué contar.
 */
export async function download(path: string): Promise<void> {
  const response = await fetch(path, { credentials: "same-origin" });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as { detail?: string; error?: string };
      detail = body.detail ?? body.error ?? detail;
    } catch {
      // Sin cuerpo JSON queda el statusText.
    }
    if (response.status === 401) sessionLost();
    throw new ApiError(response.status, detail);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const enlace = document.createElement("a");
    enlace.href = url;
    enlace.download = filenameFrom(response.headers.get("Content-Disposition"));
    document.body.append(enlace);
    enlace.click();
    enlace.remove();
  } finally {
    // Sin revocar, cada descarga deja el archivo entero retenido en memoria
    // mientras la pestaña siga abierta.
    URL.revokeObjectURL(url);
  }
}

/** Nombre que puso el servidor en `Content-Disposition`. */
function filenameFrom(header: string | null): string {
  if (!header) return "reporte";
  // `filename*=UTF-8''…` tiene precedencia sobre `filename=` cuando viene.
  const extendido = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extendido) {
    try {
      return decodeURIComponent(extendido[1]);
    } catch {
      // Un porcentaje suelto no debe impedir la descarga.
    }
  }
  const simple = /filename="?([^";]+)"?/i.exec(header);
  return simple ? simple[1] : "reporte";
}

function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export interface PeriodFilter {
  periodFrom?: string;
  periodTo?: string;
}

/**
 * Toda escritura pide su PROPIO token CSRF, justo antes de mandarla.
 *
 * Los tokens son de un solo uso, y hasta ahora el único emisor era `/config`,
 * que la interfaz pide una vez al cargar. Consecuencia: el membrete, el logo,
 * el horario y los colores se guardaban exactamente una vez por recarga; el
 * segundo intento moría con «token CSRF inválido o ya usado». Arrastrar el
 * token por props desde `Config` era el error, así que ninguna función de
 * escritura lo recibe ya: se lo busca ella.
 */
async function escribir<T>(path: string, cuerpo: Record<string, unknown>): Promise<T> {
  const { csrf_token } = await request<{ csrf_token: string }>("/api/csrf");
  return request<T>(path, {
    method: "POST",
    body: JSON.stringify({ ...cuerpo, csrf_token }),
  });
}

export const api = {
  /** Quién soy. Es la única llamada que funciona sin sesión. */
  session: () => request<SessionInfo>("/api/session"),

  /** The saved colors, WITHOUT a session, so the gate is not painted with a
   *  different palette than the rest of the platform. Colors only — see
   *  `brand_appearance` in `routes.py`. */
  brandAppearance: () => request<BrandAppearance>("/api/brand/appearance"),

  /** Rutas de sesión: el navegador NAVEGA a ellas, no las pide por fetch.
   *  `/login` es la PÁGINA del portón; `/auth/start` es lo que arranca el
   *  flujo. Están separadas para que un error no rebote contra Auth0 en
   *  bucle: ver la nota en `api/auth_routes.py`. */
  loginUrl: (next = "/") => `/auth/start${query({ next })}`,
  logoutUrl: () => "/logout",

  config: () => request<Config>("/api/config"),

  overview: () => request<Overview>("/api/overview"),

  runs: (limit = 25) => request<RunSummary[]>(`/api/runs${query({ limit })}`),

  run: (id: string) => request<RunDetail>(`/api/runs/${id}`),

  statement: (key: string, period: PeriodFilter = {}) =>
    request<StatementLine[]>(
      `/api/statements/${key}${query({
        period_from: period.periodFrom,
        period_to: period.periodTo,
      })}`,
    ),

  report: (
    key: string,
    period: PeriodFilter = {},
    limit = 200,
    offset = 0,
  ) =>
    request<ReportPage>(
      `/api/reports/${key}${query({
        period_from: period.periodFrom,
        period_to: period.periodTo,
        limit,
        offset,
      })}`,
    ),

  /** El paquete de cierre completo para un período, en una sola llamada.
   *
   *  Se pide entero a propósito: la portada, el tablero, el ESF, su anexo y las
   *  razones tienen que salir de la MISMA construcción. Pedir cada pieza por
   *  separado es lo que permitía que la hoja de cálculo tuviera un tablero
   *  dividiendo entre 55,000 mientras el estado de al lado decía 60,256,097.21.
   */
  closing: (period: PeriodFilter = {}) =>
    request<ClosingPackage>(
      `/api/closing${query({
        period_from: period.periodFrom,
        period_to: period.periodTo,
      })}`,
    ),

  /** Paquete de cierre en XLSX o PDF, con el orden y el formato que se firma. */
  closingPackageUrl: (
    fmt: "xlsx" | "pdf",
    period: PeriodFilter = {},
    support = false,
  ) =>
    `/api/export/closing-package${query({
      fmt,
      period_from: period.periodFrom,
      period_to: period.periodTo,
      support: support ? "true" : undefined,
    })}`,

  /** Un solo Excel con todos los reportes, una hoja por reporte. */
  workbookUrl: (period: PeriodFilter = {}) =>
    `/api/export/workbook${query({
      period_from: period.periodFrom,
      period_to: period.periodTo,
    })}`,

  /**
   * `flat` pide la tabla plana donde el reporte tiene presentación propia.
   * Solo el Balance de Comprobación la tiene; en los demás no cambia nada.
   */
  downloadUrl: (
    key: string,
    fmt: "xlsx" | "pdf",
    period: PeriodFilter = {},
    flat = false,
    /** El Comprobante de Partidas: una PRESENTACIÓN del Libro Diario. */
    voucher = false,
  ) =>
    `/api/reports/${key}/download${query({
      fmt,
      period_from: period.periodFrom,
      period_to: period.periodTo,
      flat: flat ? "true" : undefined,
      voucher: voucher ? "true" : undefined,
    })}`,

  /**
   * Un anexo del F-07 en CSV, listo para el portal del Ministerio.
   *
   * El período es OBLIGATORIO, y `Required<PeriodFilter>` es lo que lo hace
   * cierto. Sin valor por defecto solo se exigía el OBJETO: `f07AnexoUrl("1",
   * {})` compilaba y producía una exportación sin cota, o sea operaciones de
   * un mes en la declaración de otro. Ahora los dos extremos son obligatorios y
   * el compilador rechaza la llamada, que es donde tiene que fallar.
   */
  f07AnexoUrl: (anexo: string, period: Required<PeriodFilter>) =>
    `/api/export/f07/${anexo}${query({
      period_from: period.periodFrom,
      period_to: period.periodTo,
    })}`,

  exceptions: (severity?: Severity, limit = 500) =>
    request<DataException[]>(`/api/exceptions${query({ severity, limit })}`),

  mappingAudit: (lineId?: string) =>
    request<MappingAuditRow[]>(`/api/mapping/audit${query({ line_id: lineId })}`),

  drilldown: (lineId: string, limit = 500) =>
    request<DrilldownRow[]>(`/api/drilldown/${lineId}${query({ limit })}`),

  /** El árbol de un reporte: renglones, grupos y cuentas. NUNCA apuntes.
   *
   *  Que los apuntes no vengan aquí es una frontera de seguridad además de una
   *  decisión de volumen: quien no tiene `journal:read` llega hasta la cuenta y
   *  no puede alcanzar el Libro Diario por esta vía. */
  reportTree: (
    key: string,
    options: {
      mode?: TreeMode;
      fullOdooChain?: boolean;
      showZero?: boolean;
    } & PeriodFilter = {},
  ) =>
    request<ReportTree>(
      `/api/reports/${key}/tree${query({
        mode: options.mode,
        full_odoo_chain: options.fullOdooChain ? "true" : undefined,
        show_zero: options.showZero ? "true" : undefined,
        period_from: options.periodFrom,
        period_to: options.periodTo,
      })}`,
    ),

  /** Apuntes de un nodo, con su contribución al reporte. Exige `journal:read`. */
  nodeEntries: (
    key: string,
    nodeId: string,
    period: PeriodFilter = {},
    limit = 100,
    offset = 0,
  ) =>
    request<EntryPage>(
      `/api/reports/${key}/tree/${encodeURIComponent(nodeId)}/entries${query({
        period_from: period.periodFrom,
        period_to: period.periodTo,
        limit,
        offset,
      })}`,
    ),

  /** El árbol exportado al nivel pedido, lo haya cargado o no el navegador. */
  reportTreeExportUrl: (
    key: string,
    level: ExportLevel,
    period: PeriodFilter = {},
    showZero = false,
  ) =>
    `/api/export/report-tree${query({
      key,
      level,
      show_zero: showZero ? "true" : undefined,
      period_from: period.periodFrom,
      period_to: period.periodTo,
    })}`,

  /** Diferencias entre el mapping oficial y los account.group de Odoo. */
  mappingDivergences: () =>
    request<MappingDivergence[]>("/api/mapping/divergences"),

  /** Cuentas nuevas, aisladas y en conflicto, con el historial de decisiones. */
  classifications: () => request<ClassificationScreen>("/api/mapping/classifications"),

  /** El contador decide dónde va una cuenta. Exige `mapping:write`. */
  approveClassification: (cuerpo: ClassificationRequest) =>
    escribir<ClassificationRevision>(
      "/api/mapping/classifications",
      cuerpo as unknown as Record<string, unknown>,
    ),

  /** El membrete vigente y su historial de cambios. Exige `settings:manage`. */
  identity: () => request<Identity>("/api/settings/identity"),

  saveIdentity: (identity: Partial<Identity>) =>
    escribir<{ revision: number }>("/api/settings/identity", { ...identity }),

  /** El logo va como base64 en JSON, igual que el resto de la API. */
  saveLogo: (contentBase64: string, contentType: string) =>
    escribir<{ has_logo: boolean; byte_size: number }>("/api/settings/logo", {
      content_base64: contentBase64,
      content_type: contentType,
    }),

  removeLogo: () => escribir<{ has_logo: boolean }>("/api/settings/logo", { remove: true }),

  /** El horario de sincronización automática. Exige `settings:manage`. */
  appearance: () => request<Appearance>("/api/settings/appearance"),

  saveAppearance: (apariencia: {
    preset: string;
    page_bg?: string;
    table_header?: string;
    table_zebra?: string;
    structure?: string;
    /** Parcial: lo que no venga conserva su valor. */
    documento?: Partial<DocumentInk>;
  }) => escribir<Appearance>("/api/settings/appearance", { ...apariencia }),

  schedule: () => request<Schedule>("/api/settings/schedule"),

  saveSchedule: (schedule: { enabled: boolean; hours: string[]; timezone?: string }) =>
    escribir<Schedule>("/api/settings/schedule", { ...schedule }),

  /** URL del logo. Lleva la revisión para que el navegador no sirva el viejo. */
  logoUrl: (revision: number) => `/api/settings/logo?rev=${revision}`,

  /* `idempotencyKey` NO es el token: es la marca de «esta es la misma
     petición», y tiene que sobrevivir a un doble clic. Por eso sigue viniendo
     de fuera y estable, mientras que el token se pide aquí y es de un solo
     uso. */
  /* El avance de la corrida en curso. `unknown` significa que no se pudo
     preguntar —la base puede estar reiniciándose—, que NO es lo mismo que «no
     hay corrida»: con `unknown` la pantalla conserva lo que ya mostraba. */
  /* Los comprobantes de partida ya armados. No sale por `/reports/{key}`
     porque no es una tabla: es una lista de documentos con dos bloques cada
     uno. El período es obligatorio — un comprobante por asiento sobre todo el
     histórico son miles. */
  journalVouchers: (period: PeriodFilter) =>
    request<JournalVoucher[]>(
      `/api/journal-vouchers${query({
        period_from: period.periodFrom,
        period_to: period.periodTo,
      })}`,
    ),

  syncProgress: () =>
    request<{ run: SyncProgress | null; unknown?: boolean }>("/api/sync/progress"),

  startSync: (idempotencyKey: string) =>
    escribir<{ status: string; execution: string }>("/api/sync", {
      idempotency_key: idempotencyKey,
    }),
};
